import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Form, Format, getAuthClaim } from "@redriver/cinnamon";
import classNames from "classnames";
import {
  FixedInput,
  FixedBoolean,
  SectionTotal,
  FixedInputResponsiveLabel,
} from "components/forms";
import {
  FunderType,
  UserType,
  SheetViewType,
} from "features/../../../shared/constants/enums";
import { useSheetCalcInfo } from "features/Sheets";
import FundingSection from "./FundingSection";
import SheetStep from "../SheetStep";
import {
  getSheetCapitalState,
  getCalculationData,
  getSheetState,
} from "../../selectors";
import { shouldSheetBeSavedBeforeDownload } from "../helpers";
import { cannotCalcMessages, canCalculate } from "../actions";
import { FunderTypeAvailability } from "features/../../../shared/constants/enums/FunderType";
import { useTranslation } from "react-i18next";
import {
  UserTypeCheck,
  withPermissions,
} from "features/../../../shared/components/auth";
import { CapitalInfoModal } from "./CapitalInfoModal";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { withImpersonatedPermissions } from "modules/ImpersonatedUserPermissions";
import { CalcTableType } from "mortar/Principal/Enums";

const funderTypeSelectedFields = {
  [CalcTableType.Standard]: {
    [FunderType.Funder]: "funderSelected",
    [FunderType.Dealer]: "dealerFunderSelected",
    [FunderType.CoFunded]: "cofundedSelected",
  },
  [CalcTableType.SoftwareAssignment]: {
    [FunderType.Funder]: "funderSoftwareSelected",
    [FunderType.Dealer]: "dealerFunderSoftwareSelected",
    [FunderType.CoFunded]: "cofundedSoftwareSelected",
  },
};

const findFunderCalcSettings = (
  funderTypeSettings,
  funderType,
  calcTableType,
) =>
  funderTypeSettings?.find(
    (s) => s.funderType === funderType && s.calcTableType === calcTableType,
  );

const FunderTypeOptions = ({
  label,
  calcTableType,
  isFunderTypeAvailable,
  hideFixedIndicator,
  totals,
  t,
}) => (
  <Form.Group className="funder-types">
    <div className="funder-type-label">
      <p className="field-text">{label}</p>{" "}
      <span className="required-star">*</span>
    </div>
    <div className="funder-type-options">
      <div>
        {[
          [FunderType.Funder, t("funder"), totals.funder],
          [FunderType.Dealer, t("supplierFunded"), totals.dealer],
          [FunderType.CoFunded, t("coFunded"), totals.cofunded],
        ].map(([funderType, optionLabel, total]) => (
          <Form.If
            key={funderType}
            condition={() => isFunderTypeAvailable(funderType, calcTableType)}
          >
            <div className="funder-type-option">
              <FixedBoolean
                label={
                  <FixedInputResponsiveLabel
                    label={optionLabel}
                    labelClassName="not-bold"
                  />
                }
                field={funderTypeSelectedFields[calcTableType][funderType]}
                hideIndicator={hideFixedIndicator}
              />
              <Format.Number
                value={total}
                format={process.env.CURRENCY_CODE}
                className={classNames(total < 0 && "negative")}
              />
            </div>
          </Form.If>
        ))}
      </div>
    </div>
  </Form.Group>
);

const CapitalStep = ({
  sheetId,
  sheetData,
  onNextStep,
  onPreviousStep,
  calculating,
  missingYieldBands,
  sheetDetailsSaving,
  preventUpdates,
  calcBreakdown,
  calcHeader,
  rrpRequired,
  displayPrevious,
  displayNext,
  displaySave,
  collapsed,
  toggleVisibility,
  reactRef,
  fundingSectionsInWidestRow,
  funderTotal,
  dealerTotal,
  cofundedTotal,
  funderSoftwareTotal,
  dealerSoftwareTotal,
  cofundedSoftwareTotal,
  volumeSelectionValue,
  userType,
  userArea,
  saveBeforeDownload,
  customerToggle,
  sheetViewType,
  calculationData,
  displayCustomerToggle = false,
  impersonatedUser,
  funderTypeSettings,
  volumeSelection_Minimum,
  volumeSelection_Maximum,
  defaultVolumeSelectionValue,
  minimumVolumeSelectionValue,
  maximumVolumeSelectionValue,
  hasPermission,
}) => {
  const { t } = useTranslation("capitalCalc");
  const { t: tCalcTableType } = useTranslation("enums", {
    keyPrefix: "calcTableType",
  });
  const {
    includeStandardCalcTable: hasStandard,
    includeSoftwareCalcTable: hasSoftwareAssignment,
    isMssa,
  } = useSheetCalcInfo();

  const standardSuffix = isMssa ? ` ${tCalcTableType("Standard")}` : "";

  const canCalculateSheet = useMemo(
    () => canCalculate(sheetData, calculationData),
    [sheetData, calculationData],
  );

  const hideFixedIndicator = !hasPermission(Targets.SheetAdmin, Actions.Edit);

  //update calcBreakdown
  calcBreakdown = {
    ...calcBreakdown,
    totalDevices: sheetData.capital.totalDevicesOnSchedule.value,
    calcHeader: {
      ...calcBreakdown.calcHeader,
      sheetType: sheetData.rates.sheetType.name,
    },
  };

  if (!sheetId || !hasPermission(Targets.Sheet, Actions.ViewCapitalCalc)) {
    return null;
  }

  const getFunderCalcSettings = (funderType, calcTableType) =>
    findFunderCalcSettings(funderTypeSettings, funderType, calcTableType);

  const isFunderTypeAvailable = (funderType, calcTableType) =>
    funderTypeSettings?.some(
      (settings) =>
        settings.funderType === funderType &&
        settings.calcTableType === calcTableType &&
        settings.available &&
        (sheetViewType === SheetViewType.IpsAdmin ||
          settings.availability === FunderTypeAvailability.All ||
          (settings.availability === FunderTypeAvailability.IPSSupplierAdmin &&
            (sheetViewType === SheetViewType.IpsAdmin ||
              sheetViewType === SheetViewType.SupplierAdmin))),
    );

  const calcInfoModal = (
    <UserTypeCheck restrictTo={UserType.HQ}>
      {sheetViewType === SheetViewType.IpsAdmin && (
        <React.Fragment>
          <CapitalInfoModal
            sheetId={sheetId}
            calcBreakdown={calcBreakdown}
            data={calcBreakdown.adminInfoReport}
            calculating={calculating}
            canCalculateSheet={canCalculateSheet}
            calcHeader={calcHeader}
            funderFundingAmount={funderTotal}
            dealerFundingAmount={dealerTotal}
            cofundedFundingAmount={cofundedTotal}
            funderFundingLabel={t("fundingSection.funder")}
            dealerFundingLabel={t("fundingSection.supplier")}
            cofundedFundingLabel={t("fundingSection.coFunded")}
            saveBeforeDownload={saveBeforeDownload}
            sheetViewType={sheetViewType}
            clientName={sheetData.clientName}
            crmCompanyId={sheetData.crmCompanyId}
          />
        </React.Fragment>
      )}
    </UserTypeCheck>
  );
  return (
    <div ref={reactRef} className="step-container">
      <SheetStep
        title={t("title")}
        headerActionBar={calcInfoModal}
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        displayPrevious={displayPrevious}
        displayNext={displayNext}
        displaySave={displaySave}
        className={classNames("capital-step", collapsed && "collapsed")}
        collapsed={collapsed}
        toggleVisibility={toggleVisibility}
        calculating={calculating}
        sheetId={sheetId}
        showCapitalError={true}
        displayCustomerToggle={displayCustomerToggle}
        customerToggle={customerToggle}
        errorMessage={cannotCalcMessages.capital}
        missingYieldBands={missingYieldBands}
      >
        <Form.Object field="capital" propagateUpdates="always">
          <div className="overall-values">
            <FixedInput
              label={
                <FixedInputResponsiveLabel
                  label={t("volumeSelection")}
                  required
                  className="lowered"
                />
              }
              field="volumeSelection"
              fluid
              formatNumber
              placeholder={t("volumeSelectionPlaceholder")}
              className="volume-selection small"
              disabled={
                sheetDetailsSaving ||
                preventUpdates ||
                (userType !== UserType.HQ &&
                  sheetData &&
                  sheetData.restrictions &&
                  !sheetData.restrictions.allowVolumeSelection)
              }
              required
              hideIndicator={hideFixedIndicator}
              minAllowValue={
                userType == UserType.HQ
                  ? volumeSelection_Minimum
                  : minimumVolumeSelectionValue
              }
              maxAllowValue={
                userType == UserType.HQ
                  ? volumeSelection_Maximum
                  : maximumVolumeSelectionValue
              }
              defaultValue={defaultVolumeSelectionValue}
            />
            <div className="funder-type-groups">
              {hasSoftwareAssignment && (
                <FunderTypeOptions
                  label={t("funderTypes")}
                  calcTableType={CalcTableType.SoftwareAssignment}
                  isFunderTypeAvailable={isFunderTypeAvailable}
                  hideFixedIndicator={hideFixedIndicator}
                  totals={{
                    funder: funderSoftwareTotal,
                    dealer: dealerSoftwareTotal,
                    cofunded: cofundedSoftwareTotal,
                  }}
                  t={t}
                />
              )}
              {hasStandard && !hasSoftwareAssignment && (
                <FunderTypeOptions
                  label={t("funderTypes")}
                  calcTableType={CalcTableType.Standard}
                  isFunderTypeAvailable={isFunderTypeAvailable}
                  hideFixedIndicator={hideFixedIndicator}
                  totals={{
                    funder: funderTotal,
                    dealer: dealerTotal,
                    cofunded: cofundedTotal,
                  }}
                  t={t}
                />
              )}
            </div>
          </div>
          <div>
            <SectionTotal
              label={t("totalCapitalInvestment")}
              className={classNames(
                "capital-totaliser",
                fundingSectionsInWidestRow == 2
                  ? "medium"
                  : fundingSectionsInWidestRow == 3
                  ? "long"
                  : "",
              )}
            >
              <Format.Number
                value={
                  cofundedTotal +
                  funderTotal +
                  dealerTotal +
                  cofundedSoftwareTotal +
                  funderSoftwareTotal +
                  dealerSoftwareTotal
                }
                format={process.env.CURRENCY_CODE}
                className={classNames(
                  "totaliser-value bold",
                  cofundedTotal +
                    funderTotal +
                    dealerTotal +
                    cofundedSoftwareTotal +
                    funderSoftwareTotal +
                    dealerSoftwareTotal <
                    0 && "negative",
                )}
              />
            </SectionTotal>
          </div>
          <div className="funding-sections">
            <div className="funding-row">
              <Form.If
                condition={({ fields }) =>
                  hasSoftwareAssignment &&
                  fields.funderSoftwareSelected.value &&
                  getFunderCalcSettings(
                    FunderType.Funder,
                    CalcTableType.SoftwareAssignment,
                  )?.available
                }
              >
                <Form.Object field="funderSoftware" propagateUpdates="always">
                  <FundingSection
                    title={`${t("funderTitle")} Software Assignment`}
                    funderType={FunderType.Funder}
                    calcTableType={CalcTableType.SoftwareAssignment}
                    calculating={calculating}
                    rrpRequired={rrpRequired}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="funder software-assignment"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>

              <Form.If
                condition={({ fields }) =>
                  hasSoftwareAssignment &&
                  fields.dealerFunderSoftwareSelected.value &&
                  getFunderCalcSettings(
                    FunderType.Dealer,
                    CalcTableType.SoftwareAssignment,
                  )?.available
                }
              >
                <Form.Object field="dealerSoftware" propagateUpdates="always">
                  <FundingSection
                    title={`${t("supplierTitle")} Software Assignment`}
                    funderType={FunderType.Dealer}
                    calcTableType={CalcTableType.SoftwareAssignment}
                    calculating={calculating}
                    rrpRequired={rrpRequired}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="supplier software-assignment"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>

              <Form.If
                condition={({ fields }) =>
                  hasSoftwareAssignment &&
                  fields.cofundedSoftwareSelected.value &&
                  getFunderCalcSettings(
                    FunderType.CoFunded,
                    CalcTableType.SoftwareAssignment,
                  )?.available
                }
              >
                <Form.Object field="cofundedSoftware" propagateUpdates="always">
                  <FundingSection
                    title={`${t("coFundedTitle")} Software Assignment`}
                    funderType={FunderType.CoFunded}
                    calcTableType={CalcTableType.SoftwareAssignment}
                    rrpRequired={rrpRequired}
                    calculating={calculating}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="cofunded software-assignment"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>
            </div>

            {hasStandard && hasSoftwareAssignment && (
              <div className="funder-type-groups additional-capital-groups">
                <FunderTypeOptions
                  label={t("additionalCapital")}
                  calcTableType={CalcTableType.Standard}
                  isFunderTypeAvailable={isFunderTypeAvailable}
                  hideFixedIndicator={hideFixedIndicator}
                  totals={{
                    funder: funderTotal,
                    dealer: dealerTotal,
                    cofunded: cofundedTotal,
                  }}
                  t={t}
                />
              </div>
            )}
            <div className="funding-row">
              <Form.If
                condition={({ fields }) =>
                  hasStandard &&
                  fields.funderSelected.value &&
                  getFunderCalcSettings(
                    FunderType.Funder,
                    CalcTableType.Standard,
                  )?.available
                }
              >
                <Form.Object field="funder" propagateUpdates="always">
                  <FundingSection
                    title={`${t("funderTitle")}${standardSuffix}`}
                    funderType={FunderType.Funder}
                    calcTableType={CalcTableType.Standard}
                    calculating={calculating}
                    rrpRequired={rrpRequired}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="funder"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>

              <Form.If
                condition={({ fields }) =>
                  hasStandard &&
                  fields.dealerFunderSelected.value &&
                  getFunderCalcSettings(
                    FunderType.Dealer,
                    CalcTableType.Standard,
                  )?.available
                }
              >
                <Form.Object field="dealer" propagateUpdates="always">
                  <FundingSection
                    title={`${t("supplierTitle")}${standardSuffix}`}
                    funderType={FunderType.Dealer}
                    calcTableType={CalcTableType.Standard}
                    calculating={calculating}
                    rrpRequired={rrpRequired}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="supplier"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>

              <Form.If
                condition={({ fields }) =>
                  hasStandard &&
                  fields.cofundedSelected.value &&
                  getFunderCalcSettings(
                    FunderType.CoFunded,
                    CalcTableType.Standard,
                  )?.available
                }
              >
                <Form.Object field="cofunded" propagateUpdates="always">
                  <FundingSection
                    title={`${t("coFundedTitle")}${standardSuffix}`}
                    funderType={FunderType.CoFunded}
                    calcTableType={CalcTableType.Standard}
                    rrpRequired={rrpRequired}
                    calculating={calculating}
                    volumeSelectionValue={volumeSelectionValue}
                    sheetDetailsSaving={sheetDetailsSaving}
                    className="cofunded"
                    userArea={userArea}
                  />
                </Form.Object>
              </Form.If>
            </div>
          </div>
        </Form.Object>
      </SheetStep>
    </div>
  );
};

const computeFunderTotal = (
  capitalData,
  totalCosts,
  isSelected,
  sheetViewType,
) => {
  if (!isSelected) return 0;
  let total = (capitalData?.settlementsTotal ?? 0) + totalCosts;
  if (sheetViewType === SheetViewType.IpsAdmin) {
    total +=
      (capitalData?.adminSettlementsTotal ?? 0) +
      (capitalData?.ipSettlementsTotal ?? 0);
  } else if (sheetViewType === SheetViewType.SupplierAdmin) {
    total += capitalData?.adminSettlementsTotal ?? 0;
  }
  return total;
};

const mapStateToProps = (state) => {
  const {
    sheetData,
    calculating,
    missingYieldBands,
    sheetDetailsSaving,
    sheetViewType,
  } = getSheetState(state);
  const {
    cofunded,
    funder,
    dealer,
    funderSelected,
    cofundedSelected,
    dealerFunderSelected,
    funderSoftwareSelected,
    cofundedSoftwareSelected,
    dealerFunderSoftwareSelected,
    volumeSelection,
    cofundedTotalCosts,
    funderTotalCosts,
    dealerTotalCosts,
    funderSoftware,
    dealerSoftware,
    cofundedSoftware,
    funderSoftwareTotalCosts,
    dealerSoftwareTotalCosts,
    cofundedSoftwareTotalCosts,
  } = getSheetCapitalState(state);
  const {
    volumeSelection_Maximum,
    volumeSelection_Minimum,
    defaultVolumeSelectionValue,
    maximumVolumeSelectionValue,
    minimumVolumeSelectionValue,
    funderTypeSettings,
  } = sheetData.defaults;

  const funderTotal = computeFunderTotal(
    funder,
    funderTotalCosts ?? 0,
    !!funderSelected.value,
    sheetViewType,
  );
  const dealerTotal = computeFunderTotal(
    dealer,
    dealerTotalCosts ?? 0,
    !!dealerFunderSelected.value,
    sheetViewType,
  );
  const cofundedTotal = computeFunderTotal(
    cofunded,
    cofundedTotalCosts ?? 0,
    !!cofundedSelected.value,
    sheetViewType,
  );
  const funderSoftwareTotal = computeFunderTotal(
    funderSoftware,
    funderSoftwareTotalCosts ?? 0,
    !!funderSoftwareSelected.value,
    sheetViewType,
  );
  const dealerSoftwareTotal = computeFunderTotal(
    dealerSoftware,
    dealerSoftwareTotalCosts ?? 0,
    !!dealerFunderSoftwareSelected.value,
    sheetViewType,
  );
  const cofundedSoftwareTotal = computeFunderTotal(
    cofundedSoftware,
    cofundedSoftwareTotalCosts ?? 0,
    !!cofundedSoftwareSelected.value,
    sheetViewType,
  );

  const countSectionsInRow = (calcTableType, calcTableIncluded, selections) =>
    calcTableIncluded
      ? selections.filter(
          ([funderType, selected]) =>
            !!selected.value &&
            findFunderCalcSettings(
              funderTypeSettings,
              funderType,
              calcTableType,
            )?.available,
        ).length
      : 0;

  const fundingSectionsInWidestRow = Math.max(
    countSectionsInRow(
      CalcTableType.SoftwareAssignment,
      sheetData.includeSoftwareCalcTable,
      [
        [FunderType.Funder, funderSoftwareSelected],
        [FunderType.Dealer, dealerFunderSoftwareSelected],
        [FunderType.CoFunded, cofundedSoftwareSelected],
      ],
    ),
    countSectionsInRow(
      CalcTableType.Standard,
      sheetData.includeStandardCalcTable,
      [
        [FunderType.Funder, funderSelected],
        [FunderType.Dealer, dealerFunderSelected],
        [FunderType.CoFunded, cofundedSelected],
      ],
    ),
  );
  const volumeSelectionValue = volumeSelection.value;

  const calculationData = getCalculationData(state);
  let rrpRequired = false;
  let calcBreakdown = {};
  let calcHeader = {};

  if (calculationData) {
    calcBreakdown = {
      ...calculationData.breakdown.capitalCalcCosts,
      yieldOverrides: {
        dealer: sheetData.ipsRates.dealerFundedOverrides?.yieldOverride,
        cofund: sheetData.ipsRates.coFundedOverrides?.yieldOverride,
      },
      funders: {
        dealer:
          dealerFunderSelected.value || dealerFunderSoftwareSelected.value,
        cofund: cofundedSelected.value || cofundedSoftwareSelected.value,
      },
      calcHeader: {
        ...calculationData.breakdown.totalCfaScheduleCharges,
      },
    };

    rrpRequired = calculationData.breakdown.capitalCalcCosts.rrpRequired;
  }

  return {
    sheetData,
    calcBreakdown,
    calcHeader,
    funderTotal,
    dealerTotal,
    cofundedTotal,
    funderSoftwareTotal,
    dealerSoftwareTotal,
    cofundedSoftwareTotal,
    fundingSectionsInWidestRow,
    rrpRequired,
    volumeSelectionValue,
    userType: getAuthClaim(state, "userType"),
    userArea: getAuthClaim(state, "userArea"),
    saveBeforeDownload: shouldSheetBeSavedBeforeDownload(sheetData),
    calculating,
    missingYieldBands,
    sheetDetailsSaving,
    preventUpdates: sheetData.preventUpdates,
    calculationData,
    sheetViewType,
    impersonatedUser: sheetData.impersonatedUser,
    funderTypeSettings,
    volumeSelection_Minimum,
    volumeSelection_Maximum,
    defaultVolumeSelectionValue,
    maximumVolumeSelectionValue,
    minimumVolumeSelectionValue,
  };
};

export default connect(
  mapStateToProps,
  null,
)(withImpersonatedPermissions(CapitalStep));
